import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { AppointmentSection } from './AppointmentSection';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Calendar, Eye, ArrowRight, Search, ChevronRight, BookOpen } from 'lucide-react';
import { Toaster } from './ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = window.location.origin;

const CATEGORY_SEO = {
  tax: {
    label: 'Tax',
    slug: 'tax',
    title: 'Tax Planning & Compliance Guide | GST, TDS, Income Tax',
    description: 'Expert guidance on GST filing, TDS compliance, income tax planning, and tax-saving strategies for Indian businesses. Practical advice from certified Chartered Accountants.',
    keywords: 'tax planning India, GST compliance, TDS return filing, income tax, advance tax, tax saving, chartered accountant tax advice',
    heading: 'Tax Planning & Compliance',
    subheading: 'Navigate India\'s tax landscape with confidence. Expert advice on GST, TDS, income tax, and strategic tax planning from practicing Chartered Accountants.',
    color: 'bg-purple-100 text-purple-700',
    accentColor: 'from-purple-600 to-violet-600',
    bgColor: 'from-purple-50 to-white',
  },
  finance: {
    label: 'Finance',
    slug: 'finance',
    title: 'Finance Management Tips & Strategies | Virtual CFO Insights',
    description: 'Financial management strategies, cash flow optimization, budgeting tips, and CFO-level insights for growing businesses in India. From NSRM & Associates\' expert CA team.',
    keywords: 'finance management, cash flow optimization, budgeting, financial statements, MIS reports, virtual CFO, business finance India',
    heading: 'Finance Management',
    subheading: 'Strategic financial insights to help your business grow. Cash flow optimization, budgeting frameworks, and CFO-level thinking — made accessible.',
    color: 'bg-blue-100 text-blue-700',
    accentColor: 'from-blue-600 to-cyan-600',
    bgColor: 'from-blue-50 to-white',
  },
  compliance: {
    label: 'Compliance',
    slug: 'compliance',
    title: 'Business Compliance Guide | MCA, ROC, FEMA Filings',
    description: 'Stay compliant with MCA/ROC filings, FEMA regulations, annual returns, and statutory requirements. Step-by-step guides from NSRM & Associates\' Chartered Accountants.',
    keywords: 'business compliance India, MCA filing, ROC annual return, FEMA compliance, statutory audit, company law, compliance calendar',
    heading: 'Business Compliance',
    subheading: 'Never miss a filing deadline. Comprehensive guides on MCA, ROC, FEMA, and statutory compliance requirements for Indian companies.',
    color: 'bg-green-100 text-green-700',
    accentColor: 'from-green-600 to-emerald-600',
    bgColor: 'from-green-50 to-white',
  },
  payroll: {
    label: 'Payroll',
    slug: 'payroll',
    title: 'Payroll Management & PF/ESI Compliance Guide',
    description: 'Complete payroll processing guide: PF/ESI compliance, salary structuring, payslip generation, and employee tax planning for Indian businesses.',
    keywords: 'payroll management India, PF compliance, ESI registration, salary structuring, payslip generation, employee tax planning, payroll outsourcing',
    heading: 'Payroll & HR Compliance',
    subheading: 'Simplify payroll processing. Expert guidance on PF/ESI, salary structuring, statutory deductions, and employee compliance.',
    color: 'bg-orange-100 text-orange-700',
    accentColor: 'from-orange-600 to-amber-600',
    bgColor: 'from-orange-50 to-white',
  },
  fdi: {
    label: 'FDI & India Entry',
    slug: 'fdi',
    title: 'FDI & India Entry Guide | Foreign Investment Compliance',
    description: 'Everything foreign companies need to know about entering India: FDI routes, company incorporation, FEMA compliance, RBI reporting, and entry-exit strategies.',
    keywords: 'FDI India, foreign investment India, company incorporation India, FEMA compliance, RBI reporting, India entry strategy, foreign subsidiary India',
    heading: 'FDI & India Entry',
    subheading: 'Your complete guide to setting up and operating in India. FDI routes, incorporation, FEMA compliance, and strategic advisory for foreign companies.',
    color: 'bg-emerald-100 text-emerald-700',
    accentColor: 'from-emerald-600 to-teal-600',
    bgColor: 'from-emerald-50 to-white',
  },
  business_tips: {
    label: 'Business Tips',
    slug: 'business_tips',
    title: 'Business Growth Tips & Strategies for Indian Companies',
    description: 'Actionable business growth strategies, operational efficiency tips, and entrepreneurial advice tailored for Indian SMEs and startups.',
    keywords: 'business tips India, startup advice, growth strategy, operational efficiency, entrepreneurship, business scaling, SME tips',
    heading: 'Business Growth Tips',
    subheading: 'Proven strategies to scale your business. Operational insights, growth frameworks, and practical advice from finance professionals who\'ve seen it all.',
    color: 'bg-pink-100 text-pink-700',
    accentColor: 'from-pink-600 to-rose-600',
    bgColor: 'from-pink-50 to-white',
  },
  case_study: {
    label: 'Case Studies',
    slug: 'case_study',
    title: 'Client Case Studies | Finance & Accounting Success Stories',
    description: 'Real success stories showing how Virtual CFO Consultancy helped businesses streamline finance, reduce costs, and achieve compliance. See measurable results.',
    keywords: 'accounting case study, finance outsourcing results, virtual CFO success story, business transformation, compliance improvement',
    heading: 'Client Case Studies',
    subheading: 'Real results from real businesses. See how we\'ve helped companies streamline their finances, achieve compliance, and accelerate growth.',
    color: 'bg-indigo-100 text-indigo-700',
    accentColor: 'from-indigo-600 to-purple-600',
    bgColor: 'from-indigo-50 to-white',
  },
  quick_read: {
    label: 'Quick Reads',
    slug: 'quick_read',
    title: 'Quick Finance Tips | 2-Minute Business Insights',
    description: 'Bite-sized finance and accounting tips you can read in under 3 minutes. Quick, actionable insights on GST, tax, compliance, and business finance.',
    keywords: 'quick finance tips, short accounting articles, 2 minute read finance, quick business tips, bite-sized tax advice',
    heading: 'Quick Reads',
    subheading: 'Finance insights in under 3 minutes. Quick, actionable tips on GST, tax, compliance, and business finance for busy founders.',
    color: 'bg-amber-100 text-amber-700',
    accentColor: 'from-amber-600 to-yellow-600',
    bgColor: 'from-amber-50 to-white',
  },
};

const ALL_CATEGORIES = Object.values(CATEGORY_SEO);

export const BlogCategoryPage = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isAppointmentOpen, setIsAppointmentOpen] = useState(false);

  const seo = CATEGORY_SEO[category];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!seo) {
      navigate('/blogs', { replace: true });
      return;
    }
    document.title = `${seo.title} | Virtual CFO Consultancy`;
    fetchBlogs();
  }, [category]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/blogs/?limit=50&category=${category}`);
      if (response.ok) {
        const data = await response.json();
        setBlogs(data.blogs || []);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (cat) => {
    return CATEGORY_SEO[cat]?.color || 'bg-gray-100 text-gray-700';
  };

  const formatDate = (blog) => {
    const dateStr = blog.publishedAt || blog.createdAt;
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getReadTime = (blog) => {
    const wc = (blog.excerpt || '').split(/\s+/).length;
    return wc > 80 ? '3 min' : wc > 40 ? '2 min' : '1 min';
  };

  const filteredBlogs = blogs.filter(blog => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return blog.title.toLowerCase().includes(q) || blog.excerpt.toLowerCase().includes(q) || blog.tags.some(t => t.toLowerCase().includes(q));
  });

  if (!seo) return null;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${seo.heading} Articles | Virtual CFO Consultancy`,
    "description": seo.description,
    "url": `${SITE_URL}/blogs/${seo.slug}`,
    "isPartOf": { "@type": "Blog", "name": "Virtual CFO Consultancy Finance Blog", "url": `${SITE_URL}/blogs` },
    "publisher": {
      "@type": "Organization",
      "name": "NSRM Consultants India Private Limited",
      "logo": { "@type": "ImageObject", "url": `${SITE_URL}/logo192.png` }
    },
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": blogs.length,
      "itemListElement": blogs.slice(0, 10).map((blog, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "url": `${SITE_URL}/blog/${blog.slug}`,
        "name": blog.title
      }))
    }
  };

  const otherCategories = ALL_CATEGORIES.filter(c => c.slug !== category);

  return (
    <div className="min-h-screen bg-white" data-testid={`blog-category-page-${category}`}>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={`${seo.title} | Virtual CFO Consultancy`} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={`${SITE_URL}/blogs/${seo.slug}`} />
      <meta property="og:image" content={`${SITE_URL}/og-image.jpg`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${seo.title} | Virtual CFO Consultancy`} />
      <meta name="twitter:description" content={seo.description} />
      <link rel="canonical" href={`${SITE_URL}/blogs/${seo.slug}`} />
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>

      <Header onBookAppointment={() => setIsAppointmentOpen(true)} />

      {/* Hero */}
      <section className={`pt-24 sm:pt-32 pb-8 sm:pb-12 px-4 sm:px-6 bg-gradient-to-br ${seo.bgColor}`}>
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-6" data-testid="category-breadcrumb">
            <Link to="/" className="hover:text-emerald-600 transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/blogs" className="hover:text-emerald-600 transition-colors">Blog</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-gray-900 font-medium">{seo.label}</span>
          </nav>

          <div className="text-center">
            <Badge className={`${seo.color} mb-4 text-sm px-4 py-1.5`} data-testid="category-badge">
              <BookOpen className="w-3.5 h-3.5 mr-1.5" />
              {seo.label}
            </Badge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              {seo.heading}
              <span className={`block bg-gradient-to-r ${seo.accentColor} bg-clip-text text-transparent`}>
                Expert Articles
              </span>
            </h1>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto mb-6 sm:mb-8">
              {seo.subheading}
            </p>

            {/* Search */}
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder={`Search ${seo.label.toLowerCase()} articles...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-6 text-lg rounded-full border-2 border-gray-200 focus:border-emerald-500"
                data-testid="category-search-input"
              />
            </div>

            {!loading && (
              <p className="mt-4 text-sm text-gray-500" data-testid="category-article-count">
                {filteredBlogs.length} article{filteredBlogs.length !== 1 ? 's' : ''} in {seo.label}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-8 sm:py-16 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={`skeleton-${i}`} className="animate-pulse">
                  <div className="h-48 bg-gray-200 rounded-t-xl" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-full" />
                    <div className="h-4 bg-gray-200 rounded w-5/6" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredBlogs.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600 mb-4">No {seo.label.toLowerCase()} articles found.</p>
              <Button variant="outline" onClick={() => setSearchQuery('')}>Clear Search</Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog) => (
                <Link
                  key={blog.blogId}
                  to={`/blog/${blog.slug}`}
                  className="no-underline text-inherit"
                  data-testid={`blog-link-${blog.slug}`}
                >
                  <Card
                    data-testid={`blog-card-${blog.slug}`}
                    className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer overflow-hidden h-full"
                  >
                    {blog.featuredImage && (
                      <div className="relative h-48 overflow-hidden">
                        <img src={blog.featuredImage} alt={blog.title} loading="lazy" width="400" height="192" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
                      </div>
                    )}
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={getCategoryColor(blog.category)}>
                          {blog.category === 'fdi' ? 'FDI' : blog.category.replace('_', ' ')}
                        </Badge>
                        <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50">
                          {getReadTime(blog)} read
                        </Badge>
                      </div>
                      <CardTitle className="text-lg group-hover:text-emerald-600 transition-colors line-clamp-2">{blog.title}</CardTitle>
                      <CardDescription className="line-clamp-2 text-sm">{blog.excerpt}</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          <span>{formatDate(blog)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Eye className="w-3.5 h-3.5" />
                          <span>{blog.views}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {blog.tags.slice(0, 2).map((tag, idx) => (
                          <Badge key={`tag-${tag}-${idx}`} variant="outline" className="text-xs px-2 py-0">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <span className="inline-flex items-center text-sm font-medium group-hover:text-emerald-600 transition-colors">
                        Read Article <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Browse Other Categories */}
      <section className="py-10 sm:py-14 px-4 sm:px-6 bg-gray-50" data-testid="other-categories-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 text-center">Browse Other Topics</h2>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/blogs" data-testid="category-link-all">
              <Button variant="outline" className="rounded-full">All Articles</Button>
            </Link>
            {otherCategories.map((cat) => (
              <Link key={cat.slug} to={`/blogs/${cat.slug}`} data-testid={`category-link-${cat.slug}`}>
                <Button variant="outline" className="rounded-full">{cat.label}</Button>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 sm:py-14 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="p-6 sm:p-10 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">Need Expert Help with {seo.label}?</h2>
            <p className="text-sm sm:text-base text-gray-600 mb-6">
              Our Chartered Accountants can help you navigate {seo.label.toLowerCase()} challenges. Get a free consultation today.
            </p>
            <Button
              onClick={() => setIsAppointmentOpen(true)}
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8"
              data-testid="category-cta-button"
            >
              Book Free Consultation <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      <Footer onBookAppointment={() => setIsAppointmentOpen(true)} />
      <AppointmentSection isOpen={isAppointmentOpen} onClose={() => setIsAppointmentOpen(false)} />
      <Toaster />
    </div>
  );
};
